import {
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  SplitButton,
} from '@fluentui/react-components';
import { ChevronDown20Regular } from '@fluentui/react-icons';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './CustomSplitButton.css';

export interface CustomSplitButtonMenuItem {
  key: string | null;
  content: React.ReactNode;
  icon?: React.ReactElement;
  id?: string;
  'data-testid'?: string;
  disabled?: boolean;
  /** Index of the menu item (auto-populated) */
  index?: number;
}

export interface CustomSplitButtonButtonProps {
  content?: React.ReactNode;
  icon?: React.ReactElement;
  disabled?: boolean;
  fluid?: boolean;
  /** @deprecated Not supported in v9. Accepted for backward compatibility. */
  loading?: boolean;
  'aria-label'?: string;
  /** Allow additional ARIA and data attributes */
  [key: `aria-${string}`]: string | undefined;
  [key: `data-${string}`]: string | undefined;
}

export interface CustomSplitButtonProps {
  /** Configuration for the primary button area */
  button?: CustomSplitButtonButtonProps;
  /** Array of menu items to display in the dropdown */
  menu?: CustomSplitButtonMenuItem[];
  /** Callback when a menu item is clicked. Receives the original event and the menu item data. */
  onMenuItemClick?: (
    event: React.SyntheticEvent<HTMLElement>,
    data: CustomSplitButtonMenuItem,
  ) => void;
  /** Use primary (accent) appearance */
  primary?: boolean;
  /** Disable the entire split button */
  disabled?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** Test ID for Cypress / Testing Library */
  'data-testid'?: string;
  /** Cypress test selector */
  'data-cy'?: string;
  /** Accessible label for the overall component */
  'aria-label'?: string;
}

const CustomSplitButton: React.FC<CustomSplitButtonProps> = ({
  button,
  menu = [],
  onMenuItemClick,
  primary,
  disabled,
  className,
  'data-testid': dataTestId,
  'data-cy': dataCy,
  'aria-label': ariaLabel,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isDisabled = disabled || button?.disabled;

  // A disabled SplitButton cannot open its menu, and wrapping a disabled element in
  // Fluent's MenuTrigger triggers a "useTriggerElement(): Disabled elements should [not] be
  // used as a trigger" warning (pointer events are ignored on disabled elements). So when the
  // button is disabled, render the SplitButton on its own without the Menu/MenuTrigger wrapper.
  if (isDisabled) {
    return (
      <div className={`fui-CustomSplitButton${className ? ` ${className}` : ''}`}>
        <SplitButton
          appearance={primary ? 'primary' : undefined}
          disabled
          data-testid={dataTestId}
          data-cy={dataCy}
          aria-label={ariaLabel ?? button?.['aria-label']}
          icon={button?.icon}
          menuIcon={<ChevronDown20Regular />}
        >
          {button?.content}
        </SplitButton>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`fui-CustomSplitButton${className ? ` ${className}` : ''}`}>
      <Menu
        open={open}
        onOpenChange={(_e, data) => setOpen(data.open)}
        positioning={{ target: containerRef.current }}
      >
        <MenuTrigger disableButtonEnhancement>
          {(triggerProps) => (
            <SplitButton
              appearance={primary ? 'primary' : undefined}
              disabled={disabled || button?.disabled}
              data-testid={dataTestId}
              data-cy={dataCy}
              aria-label={ariaLabel ?? button?.['aria-label']}
              icon={button?.icon}
              menuIcon={<ChevronDown20Regular />}
              menuButton={{
                ...triggerProps,
                'aria-label': open
                  ? t('split_button_toggle_label_close', { defaultValue: 'Menü schließen' })
                  : t('split_button_toggle_label_open', { defaultValue: 'Menü öffnen' }),
              }}
              primaryActionButton={{
                // Primary button also opens the menu (all consumers use this as a menu trigger)
                onClick: () => setOpen(!open),
              }}
            >
              {button?.content}
            </SplitButton>
          )}
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {menu.map((item, index) => (
              <MenuItem
                key={item.key ?? index}
                data-testid={item['data-testid']}
                id={item.id}
                icon={item.icon}
                disabled={item.disabled}
                onClick={(event) => {
                  setOpen(false);
                  onMenuItemClick?.(event, { ...item, index });
                }}
              >
                {item.content}
              </MenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
};

export default CustomSplitButton;
